import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Loader2, Mail, MessageCircle, Clock, Lock } from "lucide-react";

const METHOD_LABEL = (u) => {
  const hasPw = !!u?.has_password || (u?.email && u?.auth_method !== "whatsapp");
  if (u?.email && u?.phone) return `E-mail${hasPw ? " + mot de passe" : ""} · WhatsApp`;
  if (u?.phone) return "WhatsApp uniquement (code par message)";
  return `E-mail${hasPw ? " + mot de passe" : ""}`;
};

// Read-only sign-in identity block (all admins) + super-admin editor with forced logout.
export const UserIdentifiersEditor = ({ user, lastLogin, isSuper, onSaved, presetPhone, presetEmail, ticketId }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(presetEmail || user?.email || "");
  const [phone, setPhone] = useState(presetPhone || user?.phone || "");
  const [reason, setReason] = useState(ticketId ? `Récupération de compte — ticket ${ticketId}` : "");
  const [busy, setBusy] = useState(false);
  const changed = (email.trim().toLowerCase() !== (user?.email || "")) || (phone.trim() !== (user?.phone || ""));
  const save = async () => {
    if (reason.trim().length < 3) { toast.error("Indique le motif (vérification d'identité effectuée, ticket…)."); return; }
    if (!changed) { toast.error("Aucun changement."); return; }
    setBusy(true);
    try {
      const body = { reason: reason.trim(), ticket_id: ticketId || undefined };
      if (email.trim().toLowerCase() !== (user?.email || "")) body.email = email.trim();
      if (phone.trim() !== (user?.phone || "")) body.phone = phone.trim();
      const { data } = await api.patch(`/admin/users/${user.id}/identifiers`, body);
      toast.success(`Identifiants mis à jour ✓ — ${Object.keys(data.changes || {}).map((k) => (k === "email" ? "e-mail" : "numéro")).join(" + ")} · sessions fermées`);
      setOpen(false); onSaved?.(data);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="user-identifiers-block">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] font-semibold flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}><KeyRound size={12} /> Identifiants de connexion</div>
        {isSuper ? (
          <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => setOpen(true)} data-testid="user-identifiers-edit">Modifier</Button>
        ) : (
          <span className="text-[10px] flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><Lock size={10} /> super admin uniquement</span>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="flex items-center gap-1.5 min-w-0"><Mail size={12} className="shrink-0" /> <span className="truncate" data-testid="user-ident-email">{user?.email || <i style={{ color: "var(--yalola-text-muted)" }}>aucun e-mail</i>}</span></div>
        <div className="flex items-center gap-1.5 min-w-0"><MessageCircle size={12} className="shrink-0" /> <span className="truncate" data-testid="user-ident-phone">{user?.phone || <i style={{ color: "var(--yalola-text-muted)" }}>aucun numéro WhatsApp</i>}</span></div>
        <div data-testid="user-ident-method"><b>Méthode :</b> {METHOD_LABEL(user)}</div>
        <div className="flex items-center gap-1.5" data-testid="user-ident-last-login"><Clock size={12} /> Dernière connexion : {lastLogin?.created_at ? `${new Date(lastLogin.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}${lastLogin.method ? ` · ${lastLogin.method}` : ""}${lastLogin.country ? ` · ${lastLogin.country}` : ""}` : "—"}</div>
      </div>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="sm:max-w-md" data-testid="user-identifiers-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Modifier les identifiants de connexion</DialogTitle>
              <DialogDescription>{user?.name} — après enregistrement, <b>toutes ses sessions sont fermées</b> et ses appareils de confiance oubliés : il devra se reconnecter avec le nouvel identifiant (code de sécurité).</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Adresse e-mail</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Laisser tel quel pour ne pas changer" data-testid="user-ident-email-input" /></div>
              <div><Label>Numéro WhatsApp (format international)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+243…" data-testid="user-ident-phone-input" /></div>
              <div><Label>Motif (obligatoire, journalisé)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. : identité vérifiée via ticket support n°…" data-testid="user-ident-reason" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-full" onClick={() => setOpen(false)} data-testid="user-ident-cancel">Annuler</Button>
              <Button className="pill-primary rounded-full" onClick={save} disabled={busy} data-testid="user-ident-save">{busy ? <Loader2 size={14} className="animate-spin" /> : "Enregistrer et déconnecter"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
